/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable react/jsx-props-no-spreading */
import { TextField, Autocomplete, CircularProgress, Stack } from "@mui/material"
import match from "autosuggest-highlight/match"
import parse from "autosuggest-highlight/parse"
import { ReactElement } from "react"
import { Control, Controller } from "react-hook-form"

import { useScreenSize } from "../../../hooks/useScreenSize"
import {
    INPUT_HELP_TOOLTIP_SPACING,
    ITEM_BORDER_MULTIPLIER,
} from "../../../themes/defaultThemeParams"
import HelpIcon from "../../helper/helpIcon"

// TODO - needs to be DRY
type TValidatedAutocompleteProps = {
    control: Control
    label: string
    registerName: string
    required: boolean
    options: readonly (string | number)[]
    getOptionLabel?: (option: any) => string
    onSelectOverride?: (newValue: string) => void
    loading?: boolean
    tooltip?: null | string
    disablePortal?: boolean
    disabled?: boolean
}

/**
 * Autocomplete with designed to be controlled by react-hook-form. Must be used
 * in a component with a useForm hook from react-hook-form. Register name
 * and control are exposed from useForm. This is for selecting from
 * a list of strings
 */
const ValidatedAutocomplete = ({
    control,
    label,
    registerName,
    required,
    options,
    getOptionLabel = (option) => String(option),
    onSelectOverride,
    disabled = false,
    loading = false,
    tooltip = null,
    disablePortal = true,
}: TValidatedAutocompleteProps): ReactElement => {
    const { isMob } = useScreenSize()
    const requiredRule = {
        required: { value: true, message: "This field is required" },
    }
    return (
        <Stack
            direction="row"
            spacing={INPUT_HELP_TOOLTIP_SPACING}
            width="100%"
        >
            <Controller
                name={registerName}
                control={control}
                rules={requiredRule}
                defaultValue={null}
                render={({ field, fieldState: { error } }) => (
                    <Autocomplete
                        {...field}
                        onChange={(_e, value) => {
                            if (onSelectOverride) {
                                onSelectOverride(value)
                            } else {
                                field.onChange(value)
                            }
                        }}
                        disablePortal={disablePortal}
                        fullWidth
                        disabled={disabled}
                        loading={loading}
                        options={options}
                        getOptionLabel={getOptionLabel}
                        renderOption={(props, option, { inputValue }) => {
                            const matches = match(String(option), inputValue, {
                                insideWords: true,
                            })
                            const optionLabel = getOptionLabel(option)
                            const parts = parse(String(optionLabel), matches)

                            return (
                                <li {...props}>
                                    <div>
                                        {parts.map((part, index) => (
                                            <span
                                                key={
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    part.text +
                                                    part.highlight +
                                                    index
                                                }
                                                style={{
                                                    fontWeight: part.highlight
                                                        ? 700
                                                        : 400,
                                                }}
                                            >
                                                {part.text}
                                            </span>
                                        ))}
                                    </div>
                                </li>
                            )
                        }}
                        renderInput={(params) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const {
                                InputLabelProps,
                                InputProps,
                                ...otherParams
                            } = params
                            return (
                                <TextField
                                    {...otherParams}
                                    label={label}
                                    placeholder="Click to select an option..."
                                    error={!!error}
                                    required={required}
                                    helperText={error?.message}
                                    InputProps={{
                                        ...InputProps,
                                        sx: {
                                            borderRadius:
                                                ITEM_BORDER_MULTIPLIER,
                                        },
                                        endAdornment: (
                                            <>
                                                {loading ? (
                                                    <CircularProgress
                                                        color="inherit"
                                                        size={20}
                                                    />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )
                        }}
                    />
                )}
            />
            {tooltip && !isMob && <HelpIcon tooltip={tooltip} />}
        </Stack>
    )
}

ValidatedAutocomplete.defaultProps = {
    getOptionLabel: (option: any) => String(option),
    onSelectOverride: null,
    loading: false,
    tooltip: null,
    disablePortal: true,
    disabled: false,
}

export default ValidatedAutocomplete
